import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { inr, fmtDate } from "@/lib/utils";

export default function AdminCatalog() {
  const { t } = useLang();
  const [tab, setTab] = useState("products");
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [dialog, setDialog] = useState(null); // product being edited or {} for new
  const [busy, setBusy] = useState(false);

  const [stockDialog, setStockDialog] = useState(null);
  const [registerDialog, setRegisterDialog] = useState(null);
  const [exportDialog, setExportDialog] = useState(null);
  const load = () => {
    api.get("/admin/companies").then((r) => setCompanies(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/products").then((r) => setProducts(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { load(); }, []);
  const downloadStockExcel = async (product, fromDate, toDate) => {
    try {
      setBusy(true);
      const q = new URLSearchParams({ from_date: fromDate, to_date: toDate }).toString();
      const res = await api.get('/admin/products/' + product.id + '/stock-register/export?' + q, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Stock-Register-' + (product.name || 'Product').replace(/[^a-zA-Z0-9_-]+/g, '_') + '-' + fromDate + '-to-' + toDate + '.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Stock register downloaded');
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadAllStockExcel = async (fromDate, toDate) => {
    try {
      setBusy(true);
      const q = new URLSearchParams({ from_date: fromDate, to_date: toDate }).toString();
      const res = await api.get('/admin/products/stock-register/export-all?' + q, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Sabuj-Sona-All-Stock-Registers-' + fromDate + '-to-' + toDate + '.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('All stock registers downloaded');
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const startStockExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    setExportDialog({ type: 'all', product: null, from_date: today, to_date: today });
  };

  const startProductExport = (product) => {
    const today = new Date().toISOString().slice(0, 10);
    setExportDialog({ type: 'product', product, from_date: today, to_date: today });
  };

  const runStockExport = async () => {
    const d = exportDialog;
    if (d.from_date > d.to_date) return toast.error('From Date cannot be after To Date');
    setExportDialog(null);
    if (d.type === 'all') await downloadAllStockExcel(d.from_date, d.to_date);
    else await downloadStockExcel(d.product, d.from_date, d.to_date);
  };

  const openRegister = async (p) => { try { setBusy(true); const r = await api.get("/admin/products/" + p.id + "/stock-register"); setRegisterDialog(r.data); } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); } };
  const addStock = async () => { const d = stockDialog; if (!d || Number(d.quantity_mt) <= 0) return toast.error("Enter received quantity"); try { setBusy(true); await api.post("/admin/products/" + d.product_id + "/stock-receipt", { quantity_mt: Number(d.quantity_mt), receipt_no: d.receipt_no, receipt_date: d.receipt_date, from_whom: d.from_whom, remarks: d.remarks }); toast.success("Stock added successfully"); setStockDialog(null); load(); } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); } };

  const addCompany = async () => {
    if (!newCompany.trim()) return toast.error("Enter a company name");
    try {
      await api.post("/admin/companies", { name: newCompany });
      setNewCompany("");
      toast.success("Company added");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleCompany = async (c) => {
    try {
      await api.put(`/admin/companies/${c.id}`, { enabled: !c.enabled });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleProduct = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { enabled: !p.enabled });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveProduct = async () => {
    const d = dialog;
    if (!d.name?.trim()) return toast.error("Product name is required");
    setBusy(true);
    try {
      const payload = {
        company_id: d.company_id,
        name: d.name,
        call_for_rate: !!d.call_for_rate,
        price_per_mt: d.call_for_rate ? null : Number(d.price_per_mt ?? 0),
        stock_mt: Number(d.stock_mt ?? 0),
        truck_capacity_mt: Number(d.truck_capacity_mt ?? 7),
        low_stock_mt: Number(d.low_stock_mt ?? 5),
        enabled: d.enabled ?? true,
      };
      if (d.id) {
        await api.put(`/admin/products/${d.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success(d.id ? "Product updated" : "Product added");
      setDialog(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = companyFilter ? products.filter((p) => p.company_id === companyFilter) : products;

  return (
    <div className="space-y-6" data-testid="admin-catalog-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("catalogTitle")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("catalogSub")}</p>
        </div>
        <div className="flex gap-2">
          {["products", "companies"].map((tb) => (
            <button
              key={tb}
              data-testid={`catalog-tab-${tb}`}
              onClick={() => setTab(tb)}
              className={`text-xs font-bold px-3.5 py-1.5 rounded-full border transition-colors capitalize ${
                tab === tb ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-300"
              }`}
            >
              {tb === "products" ? t("productsTab") : t("companiesTab")}
            </button>
          ))}
        </div>
      </div>

      {tab === "companies" ? (
        <div className="sf-card p-5" data-testid="companies-table-card">
          <div className="flex gap-2 mb-4">
            <input data-testid="new-company-input" className="sf-input" placeholder={t("newCompanyPh")} value={newCompany} onChange={(e) => setNewCompany(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCompany()} />
            <button data-testid="add-company-btn" className="sf-btn-primary shrink-0" onClick={addCompany}>
              <Plus className="h-4 w-4" /> {t("addCompany")}
            </button>
          </div>
          <table className="w-full">
            <thead>
              <tr>
                <th className="sf-th">{t("companyName")}</th>
                <th className="sf-th">{t("productsCount")}</th>
                <th className="sf-th">{t("addedOn")}</th>
                <th className="sf-th text-right">{t("visibleToCustomers")}</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="sf-tr" data-testid={`company-row-${c.id}`}>
                  <td className="sf-td font-semibold text-slate-900">
                    <Building2 className="inline h-4 w-4 mr-2 text-slate-400" />
                    {c.name}
                  </td>
                  <td className="sf-td">{c.products_count}</td>
                  <td className="sf-td">{fmtDate(c.created_at)}</td>
                  <td className="sf-td text-right">
                    <Switch data-testid={`company-toggle-${c.id}`} checked={c.enabled} onCheckedChange={() => toggleCompany(c)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <select data-testid="company-filter-select" className="sf-input w-56" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
              <option value="">{t("allCompaniesFilter")}</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button data-testid="add-product-btn" className="sf-btn-primary" onClick={() => setDialog({ company_id: companies[0]?.id || "", name: "", call_for_rate: false, price_per_mt: "", stock_mt: 0, truck_capacity_mt: 7, low_stock_mt: 5, enabled: true })}>
              <Plus className="h-4 w-4" /> {t("addProduct")}
            </button>
            <button className="sf-btn-outline ml-3" disabled={busy} onClick={startStockExport}>
              Download All Stock Excel
            </button>
          </div>

          <div className="sf-card overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr>
                  <th className="sf-th">{t("companyName")}</th>
                  <th className="sf-th">{t("product")}</th>
                  <th className="sf-th">{t("pricePerMt")}</th>
                  <th className="sf-th">{t("stockMt")}</th>
                  <th className="sf-th">{t("minCol")}</th>
                  <th className="sf-th">{t("truckSizeMt")}</th>
                  <th className="sf-th text-right">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="sf-tr" data-testid={`product-row-${p.id}`}>
                    <td className="sf-td">{p.company_name}</td>
                    <td className="sf-td font-semibold text-slate-900">
                      {p.name}
                      {!p.enabled && <span className="ml-2 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold px-2 py-0.5 uppercase">{t("disabled")}</span>}
                    </td>
                    <td className="sf-td">{p.price_per_mt != null ? inr(p.price_per_mt) : <span className="rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-2 py-0.5">{t("callForRate")}</span>}</td>
                    <td className="sf-td">
                      <span className={p.stock_mt <= 0 ? "text-rose-700 font-semibold" : ""}>{p.stock_mt}</span>
                    </td>
                    <td className="sf-td">
                      {p.low_stock_mt ?? 5}
                      {Number(p.low_stock_mt ?? 5) > 0 && Number(p.stock_mt) <= Number(p.low_stock_mt ?? 5) && (
                        <span className="ml-1.5 rounded-full bg-amber-50 border border-amber-300 text-amber-900 text-[10px] font-bold px-2 py-0.5" data-testid={`low-stock-flag-${p.id}`}>
                          LOW
                        </span>
                      )}
                    </td>
                    <td className="sf-td">{p.truck_capacity_mt}</td>
                    <td className="sf-td text-right">
                      <div className="inline-flex items-center gap-3">
                        <Switch data-testid={`product-toggle-${p.id}`} checked={p.enabled} onCheckedChange={() => toggleProduct(p)} />
                        <button data-testid={`edit-product-${p.id}`} className="sf-link text-sm" onClick={() => setDialog({ ...p, call_for_rate: p.price_per_mt == null })}>
                          {t("edit")}
                        </button>
                          <button className="sf-btn-outline text-xs px-3 py-1.5" onClick={() => openRegister(p)}>Stock Register</button>
                      <button className="sf-btn-outline text-xs px-3 py-1.5" onClick={() => startProductExport(p)}>Download Excel</button>
                        <button className="sf-btn-primary text-xs px-3 py-1.5" onClick={() => setStockDialog({ product_id: p.id, product_name: p.name, quantity_mt: "", receipt_date: new Date().toISOString().slice(0, 10), receipt_no: "", from_whom: "", remarks: "" })}>Add Stock</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.id ? t("editProduct") : t("addProduct")}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="sf-label">{t("companyName")}</label>
                  <select data-testid="product-company-select" className="sf-input" value={dialog.company_id} onChange={(e) => setDialog({ ...dialog, company_id: e.target.value })}>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="sf-label">{t("productName")}</label>
                  <input data-testid="product-name-input" className="sf-input" placeholder={t("productNamePh")} value={dialog.name || ""} onChange={(e) => setDialog({ ...dialog, name: e.target.value })} />
                </div>
              </div>
                <div className="rounded-lg border border-slate-300 bg-white p-4 space-y-3">
                  <div className="text-sm font-bold text-slate-900">Price Display</div>
                  <label className="flex items-center gap-3 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50">
                    <input type="radio" name="price-display" checked={!dialog.call_for_rate} onChange={() => setDialog({ ...dialog, call_for_rate: false })} className="h-4 w-4" />
                    <span className="text-sm font-semibold text-slate-800">Show Price</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50">
                    <input type="radio" name="price-display" checked={!!dialog.call_for_rate} onChange={() => setDialog({ ...dialog, call_for_rate: true })} className="h-4 w-4" />
                    <span className="text-sm font-semibold text-slate-800">Contact Admin for Price</span>
                  </label>
                </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="sf-label">{t("pricePerMt")}</label>
                  <input data-testid="product-price-input" type="number" min="0" className="sf-input" disabled={dialog.call_for_rate} value={dialog.price_per_mt ?? ""} onChange={(e) => setDialog({ ...dialog, price_per_mt: e.target.value })} />
                </div>
                <div>
                  <label className="sf-label">{t("stockMt")}</label>
                  <input data-testid="product-stock-input" type="number" min="0" step="any" className="sf-input" value={dialog.stock_mt ?? 0} onChange={(e) => setDialog({ ...dialog, stock_mt: e.target.value })} />
                </div>
                <div>
                  <label className="sf-label">{t("truckSizeMt")}</label>
                  <input data-testid="product-truck-input" type="number" min="0" step="any" className="sf-input" value={dialog.truck_capacity_mt ?? 7} onChange={(e) => setDialog({ ...dialog, truck_capacity_mt: e.target.value })} />
                </div>
                <div>
                  <label className="sf-label">{t("minStockMt")}</label>
                  <input data-testid="product-low-stock-input" type="number" min="0" step="any" className="sf-input" value={dialog.low_stock_mt ?? 5} onChange={(e) => setDialog({ ...dialog, low_stock_mt: e.target.value })} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5">
                <span className="text-sm font-semibold text-slate-700">{t("productEnabled")}</span>
                <Switch data-testid="product-enabled-switch" checked={dialog.enabled ?? true} onCheckedChange={(v) => setDialog({ ...dialog, enabled: v })} />
              </div>
              <DialogFooter>
                <button className="sf-btn-outline" onClick={() => setDialog(null)}>
                  {t("cancel")}
                </button>
                <button data-testid="save-product-btn" className="sf-btn-primary" onClick={saveProduct} disabled={busy}>
                  {busy ? "..." : t("saveProduct")}
                </button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
        {stockDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"><div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"><h2 className="text-xl font-semibold mb-4">Add Stock - {stockDialog.product_name}</h2><div className="grid grid-cols-1 gap-3"><input type="date" value={stockDialog.receipt_date} onChange={(e) => setStockDialog({ ...stockDialog, receipt_date: e.target.value })} className="sf-input" /><input type="text" placeholder="Invoice / Receipt No." value={stockDialog.receipt_no} onChange={(e) => setStockDialog({ ...stockDialog, receipt_no: e.target.value })} className="sf-input" /><input type="number" step="0.001" min="0" placeholder="Quantity Received (MT)" value={stockDialog.quantity_mt} onChange={(e) => setStockDialog({ ...stockDialog, quantity_mt: e.target.value })} className="sf-input" /><input type="text" placeholder="From Whom Received" value={stockDialog.from_whom} onChange={(e) => setStockDialog({ ...stockDialog, from_whom: e.target.value })} className="sf-input" /><textarea placeholder="Remarks" value={stockDialog.remarks} onChange={(e) => setStockDialog({ ...stockDialog, remarks: e.target.value })} className="sf-input" /></div><div className="mt-5 flex justify-end gap-3"><button className="sf-btn" onClick={() => setStockDialog(null)}>Cancel</button><button className="sf-btn-primary" disabled={busy} onClick={addStock}>{busy ? "Saving..." : "Add Stock"}</button></div></div></div>}
        {exportDialog && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"><div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"><h2 className="text-xl font-bold text-slate-900">Download Stock Excel</h2><p className="mt-1 text-sm text-slate-500">Select any date range for the stock register.</p><div className="mt-5 grid grid-cols-1 gap-4"><div><label className="sf-label">From Date</label><input type="date" className="sf-input" value={exportDialog.from_date} onChange={(e) => setExportDialog({ ...exportDialog, from_date: e.target.value })} /></div><div><label className="sf-label">To Date</label><input type="date" className="sf-input" value={exportDialog.to_date} onChange={(e) => setExportDialog({ ...exportDialog, to_date: e.target.value })} /></div></div><div className="mt-6 flex justify-end gap-3"><button className="sf-btn-outline" onClick={() => setExportDialog(null)}>Cancel</button><button className="sf-btn-primary" disabled={busy} onClick={runStockExport}>Download Excel</button></div></div></div>}
        {registerDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"><div className="w-full max-w-[95vw] max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl"><div className="flex items-center justify-between border-b px-5 py-4"><div><h2 className="text-xl font-bold text-slate-900">Stock Register</h2><p className="text-sm text-slate-500">NAME OF THE FERTILIZER: {registerDialog.product?.name || ""}</p><p className="text-xs text-slate-500">Stock Register - Monthly transaction record</p></div><button className="sf-btn-outline" onClick={() => setRegisterDialog(null)}>Close</button></div><div className="overflow-auto p-4"><table className="min-w-[1500px] w-full border-collapse text-xs"><thead><tr className="bg-slate-100"><th className="border p-2 text-left">Date</th><th className="border p-2 text-right">Opening Stock</th><th className="border p-2 text-left">Inv. Receipts No.</th><th className="border p-2 text-right">Quantity Received</th><th className="border p-2 text-left">From Whom Received</th><th className="border p-2 text-right">TOTAL</th><th className="border p-2 text-right">Sale Quantity</th><th className="border p-2 text-left">Cash Memo / Challan No. & Date</th><th className="border p-2 text-right">Closing Stock</th><th className="border p-2 text-left">Remarks</th></tr></thead><tbody>{(registerDialog.rows || []).map((r, i) => <tr key={i}><td className="border p-2 whitespace-nowrap">{r.date}</td><td className="border p-2 text-right">{Number(r.opening_stock_mt || 0).toFixed(3)}</td><td className="border p-2">{r.receipt_no || ""}</td><td className="border p-2 text-right">{Number(r.quantity_received_mt || 0).toFixed(3)}</td><td className="border p-2">{r.from_whom || ""}</td><td className="border p-2 text-right">{Number(r.total_stock_mt || 0).toFixed(3)}</td><td className="border p-2 text-right">{Number(r.sale_quantity_mt || 0).toFixed(3)}</td><td className="border p-2">{r.challan_no_date || ""}</td><td className="border p-2 text-right font-semibold">{Number(r.closing_stock_mt || 0).toFixed(3)}</td><td className="border p-2">{r.remarks || ""}</td></tr>)}</tbody></table>{!(registerDialog.rows || []).length && <div className="py-12 text-center text-sm text-slate-500">No stock register entries found.</div>}</div></div></div>}
    </div>
  );
}
